import { StorageProvider } from "@quotientjs/browser-storage"
import { generateUUID } from "@/uuid"
import { SessionIdStore } from "@/stores/session-id"
import { SessionLastActivityStore } from "@/stores/session-last-activity"
import { AttributionContextStore } from "@/stores/attribution-context"
import { ReferrerStore } from "@/stores/referrer"
import { SESSION_TIMEOUT_MS } from "./constants"
import { resolveUrlAttribution } from "./url-attribution"
import type { AttributionContext } from "@quotientjs/core"

/**
 * Owns session and attribution state for the browser client: a rolling
 * 30-minute session ID shared across tabs via `localStorage`, and the URL
 * attribution context that travels with it.
 */
export class AnalyticsSession {
  private readonly provider: StorageProvider
  private sessionId: string
  private lastActivityAt: number | undefined
  private attributionContext: AttributionContext = {}
  // Save the page that sent the visitor when the session starts. Every event
  // uses this value because document.referrer does not change during
  // client-side navigation.
  private entryReferrerUrl: string | undefined
  // The change to retry after storage fails. A string replaces the stored
  // attribution, null removes it, and undefined means storage matches memory.
  private pendingAttribution: string | null | undefined = undefined
  // Same idea as pendingAttribution, for the referrer store.
  private pendingReferrer: string | null | undefined = undefined
  // True only when the current URL just supplied a brand new, complete
  // attribution set on this prepare() call — not when attribution was merely
  // restored from storage. Callers use this to tell "just tagged" apart from
  // "still carrying an old tag" before trusting attribution.personId.
  private isAttributionFresh = false

  constructor(provider: StorageProvider) {
    this.provider = provider
    this.sessionId = generateUUID()
  }

  get id(): string {
    return this.sessionId
  }

  get attribution(): AttributionContext {
    return this.attributionContext
  }

  get attributionIsFresh(): boolean {
    return this.isAttributionFresh
  }

  /** Returns the page that sent the visitor when this session started. */
  get referrerUrl(): string | undefined {
    return this.entryReferrerUrl
  }

  /**
   * Refreshes session activity, then restores or updates attribution from the
   * current URL. Call this once per tracked event, before building its
   * payload.
   */
  prepare(): void {
    this.isAttributionFresh = false
    this.refreshActivity()
    this.restoreAttribution()
    this.updateAttributionFromCurrentUrl()
  }

  /** Clears URL attribution — called when the session expires. */
  private clearAttribution(): void {
    this.attributionContext = {}
    this.persistAttributionClear()
  }

  /** Restores valid attribution saved earlier in the active session. */
  private restoreAttribution(): void {
    // Finish our last storage update before trusting the persisted value.
    this.retryPendingAttribution()
    if (this.pendingAttribution !== undefined) {
      return
    }

    const serializedParams = AttributionContextStore.get(this.provider)
    if (!serializedParams) {
      this.attributionContext = {}
      return
    }

    const resolution = resolveUrlAttribution({
      searchParams: new URLSearchParams(serializedParams),
    })
    if (resolution.kind === "replace") {
      this.attributionContext = resolution.context
    }
  }

  /**
   * Replaces session attribution only when the current URL contains a
   * complete valid attribution set. URLs without managed parameters retain
   * the session; malformed Quotient tags are ignored.
   */
  private updateAttributionFromCurrentUrl(): void {
    const resolution = resolveUrlAttribution({
      searchParams: new URLSearchParams(window.location.search),
    })
    if (resolution.kind === "none" || resolution.kind === "invalid") {
      return
    }

    this.attributionContext = resolution.context
    this.isAttributionFresh = true
    this.persistAttributionWrite(resolution.serializedParams)
  }

  private retryPendingAttribution(): void {
    if (this.pendingAttribution === undefined) {
      return
    }
    if (this.pendingAttribution === null) {
      this.persistAttributionClear()
    } else {
      this.persistAttributionWrite(this.pendingAttribution)
    }
  }

  private persistAttributionWrite(serializedParams: string): void {
    AttributionContextStore.set(this.provider, serializedParams)
    this.pendingAttribution = this.confirmedStorageMatches(serializedParams)
      ? undefined
      : serializedParams
  }

  private persistAttributionClear(): void {
    AttributionContextStore.remove(this.provider)
    this.pendingAttribution = this.confirmedStorageMatches(null)
      ? undefined
      : null
  }

  private confirmedStorageMatches(expected: string | null): boolean {
    // A failed read also returns null, so availability must be checked before
    // null can confirm that a removal succeeded.
    if (!this.provider.isAvailable("local")) {
      return false
    }
    return AttributionContextStore.get(this.provider) === expected
  }

  /**
   * Saves document.referrer when a new session starts, unless it's a
   * same-origin self-referral. Those show up when a session quietly
   * restarts while someone is still browsing the site (idle past the
   * 30-minute timeout, then they click something) rather than a new visitor
   * arriving — mirrors GA4's default referral exclusion for the current
   * domain, which keeps traffic-source reports limited to real external
   * sources.
   */
  private captureEntryReferrer(): void {
    const referrer =
      typeof document !== "undefined" ? document.referrer : undefined
    this.entryReferrerUrl =
      referrer && this.isExternalReferrer(referrer) ? referrer : undefined

    if (!this.provider.isAvailable("local")) {
      return
    }
    if (this.entryReferrerUrl) {
      this.persistReferrerWrite(this.entryReferrerUrl)
    } else {
      this.persistReferrerClear()
    }
  }

  private isExternalReferrer(referrer: string): boolean {
    if (typeof window === "undefined") {
      return true
    }
    try {
      return new URL(referrer).origin !== window.location.origin
    } catch {
      return true
    }
  }

  /** Loads the referrer saved for the active session. */
  private restoreEntryReferrer(): void {
    // refreshActivity() already retried this tick — a still-pending write or
    // removal means storage can't be trusted yet, so keep the in-memory value.
    if (this.pendingReferrer !== undefined) {
      return
    }
    this.entryReferrerUrl = ReferrerStore.get(this.provider) ?? undefined
  }

  private retryPendingReferrer(): void {
    if (this.pendingReferrer === undefined) {
      return
    }
    if (this.pendingReferrer === null) {
      this.persistReferrerClear()
    } else {
      this.persistReferrerWrite(this.pendingReferrer)
    }
  }

  private persistReferrerWrite(value: string): void {
    ReferrerStore.set(this.provider, value)
    this.pendingReferrer = this.confirmedReferrerMatches(value)
      ? undefined
      : value
  }

  private persistReferrerClear(): void {
    ReferrerStore.remove(this.provider)
    this.pendingReferrer = this.confirmedReferrerMatches(null)
      ? undefined
      : null
  }

  private confirmedReferrerMatches(expected: string | null): boolean {
    if (!this.provider.isAvailable("local")) {
      return false
    }
    return (ReferrerStore.get(this.provider) ?? null) === expected
  }

  /**
   * A tab that opens mid-session continues the session another tab already
   * started, and multiple open tabs stay pinned to whichever was active most
   * recently. Falls back to a plain in-memory timeout if storage isn't
   * available at all. Either way, a session that's gone quiet for 30 minutes
   * starts fresh with no inherited attribution.
   */
  private refreshActivity(): void {
    const now = Date.now()
    const isActive = (t: number | null | undefined): boolean =>
      t !== null && t !== undefined && now >= t && now - t < SESSION_TIMEOUT_MS

    // Retry unconditionally, every call, so a tab whose own write failed
    // keeps trying on later page views even while its session just
    // continues (the branch below that doesn't touch the referrer at all).
    this.retryPendingReferrer()

    if (!this.provider.isAvailable("local")) {
      if (!isActive(this.lastActivityAt)) {
        this.sessionId = generateUUID()
        this.clearAttribution()
        this.captureEntryReferrer()
      }
      this.lastActivityAt = now
      return
    }

    const storedSessionId = SessionIdStore.get(this.provider)
    const storedLastActivityAt = SessionLastActivityStore.get(this.provider)
    const storedSessionIsActive =
      storedSessionId !== null && isActive(storedLastActivityAt)
    const memorySessionIsActive = isActive(this.lastActivityAt)
    // A tab writes a newer timestamp than ours when it's been more recently active.
    const storedIsMoreRecent =
      storedLastActivityAt !== null &&
      (this.lastActivityAt === undefined ||
        storedLastActivityAt > this.lastActivityAt)

    if (
      storedSessionIsActive &&
      (!memorySessionIsActive || storedIsMoreRecent)
    ) {
      // Adopt the active session, whether it's ours from a previous tab or
      // another tab's. restoreAttribution() reloads its attribution next.
      if (storedSessionId !== this.sessionId) {
        this.attributionContext = {}
      }
      this.sessionId = storedSessionId
      this.restoreEntryReferrer()
    } else if (memorySessionIsActive) {
      SessionIdStore.set(this.provider, this.sessionId)
    } else {
      this.sessionId = generateUUID()
      SessionIdStore.set(this.provider, this.sessionId)
      this.clearAttribution()
      this.captureEntryReferrer()
    }

    this.lastActivityAt = now
    SessionLastActivityStore.set(this.provider, now)
  }
}
